import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { LoginService } from 'src/app/services/login.service';
import { CreateDoctorDTO, Doctor } from '../../dto/doctor.dto';
import { AddDoctorModalComponent } from './add-doctor-modal/add-doctor-modal.component';
import { debounceTime, switchMap } from 'rxjs/operators';
import { DoctorService, DoctorGridItem } from 'src/app/services/doctor.service';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-doctors',
  templateUrl: './doctors.component.html',
  styleUrls: ['./doctors.component.scss'],
})
export class DoctorsComponent implements OnInit {
  loading = false;
  doctorsList = new MatTableDataSource<DoctorGridItem>([]);
  searchSubject = new Subject();
  searchTerm = '';
  active: boolean | undefined;
  order: 'ASC' | 'DESC' | '' = 'ASC';
  paginatorPage = 1;
  paginatorPageSize = 20;
  paginatorLength = 0;
  suppressOverlayOnce = false;

  filters = [
    { name: 'Todos', value: undefined },
    { name: 'Ativos', value: true },
    { name: 'Inativos', value: false },
  ];

  columns = ['name', 'isActive', 'cpf', 'payment_wallet_id', 'actions'];

  syncingById: Record<number, boolean> = {};
  get isSyncing(): boolean {
    return Object.values(this.syncingById).some(Boolean);
  }
  constructor(
    private dialog: MatDialog,
    private readonly doctorService: DoctorService,
    private readonly loginService: LoginService
  ) {}

  ngOnInit(): void {
    this.searchSubject
      .pipe(
        debounceTime(500),
        switchMap(() => {
          if (!this.suppressOverlayOnce) this.loading = true;

          return this.doctorService
            .findAll({
              page: this.paginatorPage,
              limit: this.paginatorPageSize,
              searchTerm: this.searchTerm,
              active: this.active,
              order: this.order,
            })
            .pipe(
              finalize(() => {
                this.loading = false;
                this.suppressOverlayOnce = false;
              })
            );
        })
      )
      .subscribe({
        next: ({ list, total }) => {
          this.doctorsList.data = list;
          this.paginatorLength = total;
        },
        error: (error) => {
          console.log(error);
          this.loginService.showMessage('Erro ao buscar médicos', 3000);
        },
      });

    this.searchSubject.next();
  }

  onSearch() {
    this.paginatorPage = 1;
    this.searchSubject.next();
  }

  onSort(event: Sort) {
    this.paginatorPage = 1;
    this.order = event.direction.toUpperCase() as 'ASC' | 'DESC' | '';
    this.searchSubject.next();
  }

  async onChangePage(event: PageEvent): Promise<void> {
    this.paginatorPage = event?.pageIndex + 1;
    this.searchSubject.next();
  }

  openDoctorsModal(doctor?: Partial<Doctor>) {
    const open = (data?: Partial<Doctor>) => {
      const dialogRef = this.dialog.open(AddDoctorModalComponent, {
        disableClose: false,
        width: '80%',
        ...(data && { data }),
      });

      dialogRef.afterClosed().subscribe({
        next: (result: any) => {
          if (result && result.__saved) {
            this.suppressOverlayOnce = true;
            this.syncingById = {};
            this.searchSubject.next();
            return;
          }
          if (result) this.saveDoctor(result as CreateDoctorDTO);
        },
      });
    };

    if (!doctor?.cpf) {
      open(doctor);
      return;
    }

    this.loading = true;
    this.doctorService.findByCpf(doctor.cpf).subscribe({
      next: (full) => {
        this.loading = false;
        open(full ?? doctor);
      },
      error: () => {
        this.loading = false;
        open(doctor);
      },
    });
  }

  saveDoctor(doctor: CreateDoctorDTO) {
    this.doctorService.save(doctor).subscribe({
      next: () => {
        this.loginService.showMessage('Médico salvo com sucesso', 3000);
        this.searchSubject.next();
      },
      error: (error) => {
        console.log(error);
        this.loginService.showMessage('Erro ao salvar médico', 3000);
      },
    });
  }

  changeIsActive(doctor: Doctor) {
    this.doctorService.toggleActive(doctor.id, !doctor.isActive).subscribe({
      next: () => {
        this.loginService.showMessage('Médico atualizado com sucesso', 3000);
        this.searchSubject.next();
      },
      error: (error) => {
        console.log(error);
        this.loginService.showMessage('Erro ao atualizar médico', 3000);
      },
    });
  }

  syncDoctor(row: DoctorGridItem) {
    const id = Number(row.id);
    this.syncingById[id] = true;

    this.doctorService.syncAsaas(id).subscribe({
      next: () => {
        this.loginService.showMessage('Sincronizado com a Asaas', 3000);
        this.searchSubject.next();
      },
      error: (error) => {
        const backendMessage =
          error?.error?.message || 'Falha ao sincronizar com a Asaas';
        this.loginService.showMessage(backendMessage, 5000);
        this.syncingById[id] = false;
      },
      complete: () => {
        this.syncingById[id] = false;
      },
    });
  }
}
